import { type CMSProfile } from '@/app/routes/Admin/Users/users/interfaces';
import log from '@/lib/logger';
import { queryClient } from '@/query-client';
import { type AppDispatch, type RootState } from '@/store';
import { callBatch, type BatchCall } from '@/util/callBatch';
import { getNewImmutableState } from '@/util/getNewImmutableState';
import { type UnknownAction } from '@reduxjs/toolkit';
import { type Form, type SubmitContractData } from 'app/routes/Admin/Contracts/interfaces';
import { AxiosError } from 'axios';
import { DEFAULT_SPOT_USER_GROUP } from 'constants/Company';
import {
  deleteProfileDeferredUserGroup,
  deleteProfileSpotUserGroup,
  getDeferredProfileUserGroups,
  getSpotProfileUserGroups,
  postProfileSpotUserGroup,
} from 'services/accounts';
import { type User } from 'services/accounts/interfaces';
import { getChargePointGroups } from 'services/chargePoints';
import {
  createContract,
  deleteContract,
  getContracts,
  getPowerCategory,
  getUserGroupById,
  getUserGroups,
  getWhiteLabelVariables,
  updateContract,
} from 'services/company';
import {
  UserGroupKindEnum,
  type ChargepointGroup,
  type ContractOutput as Contract,
  type CurrencyEnum,
  type DocumentTypesEnum,
  type PowerCategory,
  type ProfileDeferredUserGroup,
  type ProfileSpotUserGroup,
  type UserGroup,
  type UserGroupList,
  type UserGroupWithKind,
} from 'types/swagger';
import { callNextPage } from 'util/callNextPage';

import { showSnackbarMessage } from './Common';

enum CompanyActionTypes {
  // USERS
  SET_USERS = 'company/set_users',
  // USER GROUPS
  SET_USER_GROUPS = 'company/set_user_groups',
  ADD_USER_GROUP = 'company/add_user_group',
  SET_SELECTED_USER_GROUP = 'company/set_selected_user_group',
  UPDATE_USER_GROUP_NAME = 'company/update_user_group_name',
  UPDATE_USER_GROUP_PROFILES = 'company/update_user_from_group',
  DELETE_USER_GROUP = 'company/delete_user_group',
  // PROFILE USER GROUPS
  SET_PROFILE_USER_GROUPS = 'company/set_profile_user_groups',
  ADD_PROFILE_TO_PROFILE_USER_GROUPS = 'company/add_profile_to_profile_user_group',
  REMOVE_PROFILE_FROM_PROFILE_USER_GROUPS = 'company/remove_profile_from_profile_user_group',
  // CHARGE POINTS
  SET_CHARGE_POINT_GROUPS = 'company/set_charge_point_groups',
  DELETE_CHARGE_POINT_GROUP = 'company/delete_charge_point_group',
  ADD_CHARGE_POINT_GROUP = 'company/add_charge_point_groups',
  // CONTRACTS
  SET_CONTRACTS = 'company/set_contracts',
  ADD_CONTRACT = 'company/add_contract',
  UPDATE_CONTRACT = 'company/update_contract',
  DELETE_CONTRACT = 'company/delete_contract',
  // LOADINGS
  SET_IS_FETCHING_USERS = 'company/set_is_fetching_users',
  SET_IS_FETCHING_USER_GROUP = 'company/set_is_fetching_user_group',
  SET_IS_FETCHING_USER_GROUPS = 'company/set_is_fetching_user_groups',
  SET_IS_FETCHING_CHARGE_POINT_GROUPS = 'company/set_is_fetching_charge_point_groups',
  SET_IS_FETCHING_CONTRACTS = 'company/set_is_fetching_contracts',
  SET_IS_FETCHING_WHITE_LABEL = 'company/set_is_fetching_white_labels',
  // POWER CATEGORY
  SET_POWER_CATEGORY = 'company/set_power_category',
  // WHITE LABELS
  SET_WHITE_LABEL = 'company/set_white_labels',
}

interface WhiteLabelConfig {
  currency: CurrencyEnum | null;
  default_latitude: string;
  default_longitude: string;
  default_zoom: string;
  document_types: string[];
  phone_code: string;
  timezone: string;
  vat: string;
  white_label_name: string;
}

export const setUsers = (users: User[]) => ({ type: CompanyActionTypes.SET_USERS, payload: users });

export const setProfileUserGroups = (
  spotProfileUserGroups: ProfileSpotUserGroup[],
  deferredProfileUserGroups: ProfileDeferredUserGroup[]
) => ({
  type: CompanyActionTypes.SET_PROFILE_USER_GROUPS,
  payload: { spotProfileUserGroups, deferredProfileUserGroups },
});

export const addProfileToProfileUserGroups = (
  groupId: number,
  profileUserGroup: ProfileSpotUserGroup | ProfileDeferredUserGroup
) => ({
  type: CompanyActionTypes.ADD_PROFILE_TO_PROFILE_USER_GROUPS,
  payload: { groupId, profileUserGroup },
});

export const removeProfileFromProfileUserGroups = (groupId: number, profileId: number) => ({
  type: CompanyActionTypes.REMOVE_PROFILE_FROM_PROFILE_USER_GROUPS,
  payload: { groupId, profileId },
});

export const setUserGroups = (userGroups: UserGroupList) => ({
  type: CompanyActionTypes.SET_USER_GROUPS,
  payload: userGroups,
});

export const setSelectedUserGroup = (userGroup: UserGroup) => ({
  type: CompanyActionTypes.SET_SELECTED_USER_GROUP,
  payload: userGroup,
});

export const addUserGroup = (userGroup: UserGroupWithKind) => ({
  type: CompanyActionTypes.ADD_USER_GROUP,
  payload: userGroup,
});

export const updateUserGroupName = (userGroupId: number, name: string) => ({
  type: CompanyActionTypes.UPDATE_USER_GROUP_NAME,
  payload: { userGroupId, name },
});

export const updateUserGroupProfiles = (userGroupId: number, profileIds: number[]) => ({
  type: CompanyActionTypes.UPDATE_USER_GROUP_PROFILES,
  payload: { userGroupId, profileIds },
});

export const deleteUserGroup = (userGroupId: number) => ({
  type: CompanyActionTypes.DELETE_USER_GROUP,
  payload: userGroupId,
});

export const setChargePointGroups = (chargePointGroups: ChargepointGroup[]) => ({
  type: CompanyActionTypes.SET_CHARGE_POINT_GROUPS,
  payload: chargePointGroups,
});

export const deleteChargePointGroup = (chargePointGroupId: number) => ({
  type: CompanyActionTypes.DELETE_CHARGE_POINT_GROUP,
  payload: chargePointGroupId,
});

export const addChargePointGroup = (chargePointGroup: ChargepointGroup) => ({
  type: CompanyActionTypes.ADD_CHARGE_POINT_GROUP,
  payload: chargePointGroup,
});

export const setContracts = (contracts: Contract[]) => ({
  type: CompanyActionTypes.SET_CONTRACTS,
  payload: contracts,
});

export const setIsFetchingUsers = (isFetching: boolean) => ({
  type: CompanyActionTypes.SET_IS_FETCHING_USERS,
  payload: isFetching,
});

export const setIsFetchingUserGroups = (isFetching: boolean) => ({
  type: CompanyActionTypes.SET_IS_FETCHING_USER_GROUPS,
  payload: isFetching,
});

export const setIsFetchingUserGroup = (isFetching: boolean) => ({
  type: CompanyActionTypes.SET_IS_FETCHING_USER_GROUP,
  payload: isFetching,
});

export const setIsFetchingChargePointGroups = (isFetching: boolean) => ({
  type: CompanyActionTypes.SET_IS_FETCHING_CHARGE_POINT_GROUPS,
  payload: isFetching,
});

export const setIsFetchingContracts = (isFetching: boolean) => ({
  type: CompanyActionTypes.SET_IS_FETCHING_CONTRACTS,
  payload: isFetching,
});

export const setIsFetchingWhiteLabels = (isFetching: boolean) => ({
  type: CompanyActionTypes.SET_IS_FETCHING_WHITE_LABEL,
  payload: isFetching,
});

export const addContract = (contract: Contract) => ({
  type: CompanyActionTypes.ADD_CONTRACT,
  payload: contract,
});

export const editContract = (contractId: number, contract: Form) => ({
  type: CompanyActionTypes.UPDATE_CONTRACT,
  payload: { contractId, contract },
});

export const removeContract = (contractId: number) => ({
  type: CompanyActionTypes.DELETE_CONTRACT,
  payload: { contractId },
});

export const setPowerCategory = (powerCategory: PowerCategory[]) => ({
  type: CompanyActionTypes.SET_POWER_CATEGORY,
  payload: powerCategory,
});

export const setWhiteLabelConfig = (whiteLabel: WhiteLabelConfig) => ({
  type: CompanyActionTypes.SET_WHITE_LABEL,
  payload: whiteLabel,
});

interface CompanyState {
  users: { users: User[]; byId: Record<number, User>; allUserEmailsById: Record<number, string> };
  userGroups: {
    userGroups: UserGroupList;
    spotUserGroupsById: Record<number, UserGroup>;
    deferredUserGroupsById: Record<number, UserGroup>;
    combinedUserGroups: UserGroupWithKind[];
    combinedUserGroupsById: Record<number, UserGroupWithKind>;
    allUserGroupNamesById: Record<number, string>;
    selectedUserGroup: UserGroup | null;
    defaultGroups: { spotUserGroup: UserGroup | null };
  };
  profileUserGroups: {
    spotProfileUserGroups: ProfileSpotUserGroup[];
    deferredProfileUserGroups: ProfileDeferredUserGroup[];
    byProfileId: Record<
      number,
      { spot: ProfileSpotUserGroup[]; deferred: ProfileDeferredUserGroup[] }
    >;
  };
  chargePointGroups: {
    chargePointGroups: ChargepointGroup[];
    byId: Record<number, ChargepointGroup>;
    defaultChargePointGroup: ChargepointGroup | null;
    allChargePointGroupNamesById: Record<number, string>;
  };
  contracts: {
    contracts: Contract[];
    byId: Record<number, Contract>;
    allContractNamesById: Record<number, string | null>;
  };
  isFetching: {
    users: boolean;
    userGroups: boolean;
    userGroup: boolean;
    chargePointGroups: boolean;
    contracts: boolean;
    whiteLabel: boolean;
  };
  powerCategory: { powerCategory: PowerCategory[] };
  whiteLabelConfig: WhiteLabelConfig;
}

const INIT_STATE: CompanyState = {
  users: { users: [], byId: {}, allUserEmailsById: {} },
  userGroups: {
    userGroups: { spot_user_groups: [], deferred_user_groups: [] },
    spotUserGroupsById: {},
    deferredUserGroupsById: {},
    combinedUserGroups: [],
    combinedUserGroupsById: {},
    allUserGroupNamesById: {},
    selectedUserGroup: null,
    defaultGroups: { spotUserGroup: null },
  },
  profileUserGroups: { spotProfileUserGroups: [], deferredProfileUserGroups: [], byProfileId: {} },
  chargePointGroups: {
    chargePointGroups: [],
    byId: {},
    defaultChargePointGroup: null,
    allChargePointGroupNamesById: {},
  },
  contracts: { contracts: [], byId: {}, allContractNamesById: {} },
  isFetching: {
    users: false,
    userGroups: false,
    userGroup: false,
    chargePointGroups: false,
    contracts: false,
    whiteLabel: false,
  },
  powerCategory: { powerCategory: [] },
  whiteLabelConfig: {
    currency: null,
    default_latitude: '',
    default_longitude: '',
    default_zoom: '',
    document_types: [],
    phone_code: '',
    timezone: '',
    vat: '',
    white_label_name: '',
  },
};

export default function companyReducer(state = INIT_STATE, action: UnknownAction): CompanyState {
  const { type, payload } = action;

  switch (type) {
    case CompanyActionTypes.SET_USERS: {
      return {
        ...state,
        users: {
          ...state.users,
          users: payload as User[],
          byId: {
            ...state.users.byId,
            ...Object.fromEntries((payload as User[]).map((user) => [user.id, user])),
          },
          allUserEmailsById: Object.fromEntries(
            (payload as User[]).map((user) => [user.id, user.email])
          ),
        },
      };
    }
    case CompanyActionTypes.SET_PROFILE_USER_GROUPS: {
      const profileUserGroups = payload as {
        spotProfileUserGroups: ProfileSpotUserGroup[];
        deferredProfileUserGroups: ProfileDeferredUserGroup[];
      };
      const userIds = new Set(
        [
          ...profileUserGroups.spotProfileUserGroups,
          ...profileUserGroups.deferredProfileUserGroups,
        ].map((profileUserGroup) => profileUserGroup.profile)
      );
      const profileSpotByUserId: Record<number, ProfileSpotUserGroup[]> = {};
      const profileDeferredByUserId: Record<number, ProfileDeferredUserGroup[]> = {};
      profileUserGroups.spotProfileUserGroups.forEach((profileUserGroup) => {
        if (!profileSpotByUserId[profileUserGroup.profile])
          profileSpotByUserId[profileUserGroup.profile] = [];
        profileSpotByUserId[profileUserGroup.profile] = [
          ...profileSpotByUserId[profileUserGroup.profile],
          profileUserGroup,
        ];
      });
      profileUserGroups.deferredProfileUserGroups.forEach((profileUserGroup) => {
        if (!profileDeferredByUserId[profileUserGroup.profile])
          profileDeferredByUserId[profileUserGroup.profile] = [];
        profileDeferredByUserId[profileUserGroup.profile] = [
          ...profileDeferredByUserId[profileUserGroup.profile],
          profileUserGroup,
        ];
      });
      return {
        ...state,
        profileUserGroups: {
          spotProfileUserGroups: profileUserGroups.spotProfileUserGroups,
          deferredProfileUserGroups: profileUserGroups.deferredProfileUserGroups,
          byProfileId: Object.fromEntries(
            Array.from(userIds).map((userId) => {
              return [
                userId,
                {
                  spot: profileSpotByUserId[userId] || [],
                  deferred: profileDeferredByUserId[userId] || [],
                },
              ];
            })
          ),
        },
      };
    }
    case CompanyActionTypes.ADD_PROFILE_TO_PROFILE_USER_GROUPS: {
      const { groupId, profileUserGroup } = payload as {
        groupId: number;
        profileUserGroup: ProfileSpotUserGroup | ProfileDeferredUserGroup;
      };

      const profileGroupAlreadyExists =
        state.profileUserGroups.spotProfileUserGroups.find(
          (ug) => ug.profile === profileUserGroup.profile && ug.user_group === groupId
        ) ||
        state.profileUserGroups.deferredProfileUserGroups.find(
          (ug) => ug.profile === profileUserGroup.profile && ug.user_group === groupId
        );

      const profileKind = state.userGroups.combinedUserGroups.find(
        (ug) => ug.id === groupId
      )?.user_group_kind;

      if (!profileGroupAlreadyExists || !profileKind) return state;

      return {
        ...state,
        userGroups: {
          ...state.userGroups,
          userGroups: {
            spot_user_groups:
              profileKind === UserGroupKindEnum.Spot
                ? state.userGroups.userGroups.spot_user_groups.map((sug) => {
                    if (sug.id === groupId) {
                      sug.profiles = [...sug.profiles, profileUserGroup.profile];
                    }
                    return sug;
                  })
                : state.userGroups.userGroups.spot_user_groups,
            deferred_user_groups:
              profileKind === UserGroupKindEnum.Deferred
                ? state.userGroups.userGroups.deferred_user_groups.map((dug) => {
                    if (dug.id === groupId) {
                      dug.profiles = [...dug.profiles, profileUserGroup.profile];
                    }
                    return dug;
                  })
                : state.userGroups.userGroups.deferred_user_groups,
          },
          spotUserGroupsById:
            profileKind === UserGroupKindEnum.Spot
              ? {
                  ...state.userGroups.spotUserGroupsById,
                  [groupId]: {
                    ...state.userGroups.spotUserGroupsById[groupId],
                    profiles: [
                      ...state.userGroups.spotUserGroupsById[groupId].profiles,
                      profileUserGroup.profile,
                    ],
                  },
                }
              : state.userGroups.spotUserGroupsById,
          deferredUserGroupsById:
            profileKind === UserGroupKindEnum.Deferred
              ? {
                  ...state.userGroups.deferredUserGroupsById,
                  [groupId]: {
                    ...state.userGroups.deferredUserGroupsById[groupId],
                    profiles: [
                      ...state.userGroups.deferredUserGroupsById[groupId].profiles,
                      profileUserGroup.profile,
                    ],
                  },
                }
              : state.userGroups.deferredUserGroupsById,
          combinedUserGroups: state.userGroups.combinedUserGroups.map((userGroup) => {
            if (userGroup.id === groupId) {
              userGroup.profiles = [...userGroup.profiles, profileUserGroup.profile];
              userGroup.profiles_count = Array.from(
                new Set([...userGroup.profiles, profileUserGroup.profile]).values()
              ).length;
            }
            return userGroup;
          }),
        },
        profileUserGroups: {
          ...state.profileUserGroups,
          spotProfileUserGroups:
            profileKind === UserGroupKindEnum.Spot
              ? [...state.profileUserGroups.spotProfileUserGroups, profileUserGroup]
              : state.profileUserGroups.spotProfileUserGroups,
          deferredProfileUserGroups:
            profileKind === UserGroupKindEnum.Deferred
              ? [...state.profileUserGroups.deferredProfileUserGroups, profileUserGroup]
              : state.profileUserGroups.deferredProfileUserGroups,
          byProfileId: {
            ...state.profileUserGroups.byProfileId,
            [profileUserGroup.profile]: {
              spot:
                profileKind === UserGroupKindEnum.Spot
                  ? [
                      ...state.profileUserGroups.byProfileId[profileUserGroup.profile].spot,
                      profileUserGroup,
                    ]
                  : state.profileUserGroups.byProfileId[profileUserGroup.profile].spot,
              deferred:
                profileKind === UserGroupKindEnum.Deferred
                  ? [
                      ...state.profileUserGroups.byProfileId[profileUserGroup.profile].deferred,
                      profileUserGroup,
                    ]
                  : state.profileUserGroups.byProfileId[profileUserGroup.profile].deferred,
            },
          },
        },
      };
    }
    case CompanyActionTypes.REMOVE_PROFILE_FROM_PROFILE_USER_GROUPS: {
      const { groupId, profileId } = payload as { groupId: number; profileId: number };
      const newState = getNewImmutableState(state) as CompanyState;
      const profileKind = newState.userGroups.combinedUserGroups.find(
        (ug) => ug.id === groupId
      )?.user_group_kind;
      const byProfileId = { ...newState.profileUserGroups.byProfileId };
      if (!profileKind || !byProfileId[profileId]) return newState;
      if (profileKind === UserGroupKindEnum.Spot)
        byProfileId[profileId].spot = byProfileId[profileId].spot.filter(
          (psug) => psug.user_group !== groupId
        );
      else {
        byProfileId[profileId].deferred = byProfileId[profileId].deferred.filter(
          (pdug) => pdug.user_group !== groupId
        );
      }
      return {
        ...newState,
        userGroups: {
          ...newState.userGroups,
          userGroups: {
            spot_user_groups:
              profileKind === UserGroupKindEnum.Spot
                ? newState.userGroups.userGroups.spot_user_groups.map((sug) => {
                    if (sug.id === groupId) {
                      sug.profiles = sug.profiles.filter((p) => p !== profileId);
                    }
                    return sug;
                  })
                : newState.userGroups.userGroups.spot_user_groups,
            deferred_user_groups:
              profileKind === UserGroupKindEnum.Deferred
                ? newState.userGroups.userGroups.deferred_user_groups.map((dug) => {
                    if (dug.id === groupId) {
                      dug.profiles = dug.profiles.filter((p) => p !== profileId);
                    }
                    return dug;
                  })
                : newState.userGroups.userGroups.deferred_user_groups,
          },
          combinedUserGroups: newState.userGroups.combinedUserGroups.map((userGroup) => {
            if (userGroup.id === groupId)
              userGroup.profiles = userGroup.profiles.filter((p) => p !== profileId);
            return userGroup;
          }),
          spotUserGroupsById:
            profileKind === UserGroupKindEnum.Spot
              ? {
                  ...newState.userGroups.spotUserGroupsById,
                  [groupId]: {
                    ...newState.userGroups.spotUserGroupsById[groupId],
                    profiles: newState.userGroups.spotUserGroupsById[groupId].profiles.filter(
                      (p) => p !== profileId
                    ),
                  },
                }
              : newState.userGroups.spotUserGroupsById,
          deferredUserGroupsById:
            profileKind === UserGroupKindEnum.Deferred
              ? {
                  ...newState.userGroups.deferredUserGroupsById,
                  [groupId]: {
                    ...newState.userGroups.deferredUserGroupsById[groupId],
                    profiles: newState.userGroups.deferredUserGroupsById[groupId].profiles.filter(
                      (p) => p !== profileId
                    ),
                  },
                }
              : newState.userGroups.deferredUserGroupsById,
        },
        profileUserGroups: {
          ...newState.profileUserGroups,
          spotProfileUserGroups:
            profileKind === UserGroupKindEnum.Spot
              ? newState.profileUserGroups.spotProfileUserGroups.filter(
                  (psug) => psug.profile !== profileId && psug.user_group !== groupId
                )
              : newState.profileUserGroups.spotProfileUserGroups,
          deferredProfileUserGroups:
            profileKind === UserGroupKindEnum.Deferred
              ? newState.profileUserGroups.deferredProfileUserGroups.filter(
                  (pdug) => pdug.profile !== profileId && pdug.user_group !== groupId
                )
              : newState.profileUserGroups.deferredProfileUserGroups,
          byProfileId,
        },
      };
    }
    case CompanyActionTypes.SET_USER_GROUPS: {
      const userGroupList = payload as UserGroupList;
      return {
        ...state,
        userGroups: {
          ...state.userGroups,
          userGroups: payload as UserGroupList,

          spotUserGroupsById: Object.fromEntries(
            userGroupList.spot_user_groups.map((userGroup) => [userGroup.id, userGroup])
          ),
          deferredUserGroupsById: Object.fromEntries(
            userGroupList.deferred_user_groups.map((userGroup) => [userGroup.id, userGroup])
          ),
          combinedUserGroups: [
            ...userGroupList.spot_user_groups.map((userGroup) => ({
              ...userGroup,
              user_group_kind: UserGroupKindEnum.Spot,
            })),
            ...userGroupList.deferred_user_groups.map((userGroup) => ({
              ...userGroup,
              user_group_kind: UserGroupKindEnum.Deferred,
            })),
          ],
          combinedUserGroupsById: Object.fromEntries(
            [...userGroupList.spot_user_groups, ...userGroupList.deferred_user_groups].map(
              (userGroup) => [
                userGroup.id,
                {
                  ...userGroup,
                  user_group_kind: userGroupList.spot_user_groups.find(
                    (sug) => sug.id === userGroup.id
                  )
                    ? UserGroupKindEnum.Spot
                    : UserGroupKindEnum.Deferred,
                },
              ]
            )
          ) as Record<number, UserGroupWithKind>,
          allUserGroupNamesById: Object.fromEntries(
            [...userGroupList.spot_user_groups, ...userGroupList.deferred_user_groups].map(
              (userGroup) => [userGroup.id, userGroup.name]
            )
          ),
          defaultGroups: {
            spotUserGroup:
              userGroupList.spot_user_groups.find((sug) => sug.name === DEFAULT_SPOT_USER_GROUP) ||
              null,
          },
        },
      };
    }
    case CompanyActionTypes.SET_SELECTED_USER_GROUP: {
      return {
        ...state,
        userGroups: { ...state.userGroups, selectedUserGroup: payload as UserGroup },
      };
    }

    case CompanyActionTypes.UPDATE_USER_GROUP_NAME: {
      const { userGroupId, name } = payload as { userGroupId: number; name: string };
      return {
        ...state,
        userGroups: {
          ...state.userGroups,
          spotUserGroupsById: {
            ...state.userGroups.spotUserGroupsById,
            [userGroupId]: { ...state.userGroups.spotUserGroupsById[userGroupId], name },
          },
          deferredUserGroupsById: {
            ...state.userGroups.deferredUserGroupsById,
            [userGroupId]: { ...state.userGroups.deferredUserGroupsById[userGroupId], name },
          },
          combinedUserGroups: state.userGroups.combinedUserGroups.map((userGroup) => {
            if (userGroup.id === userGroupId) {
              userGroup.name = name;
            }

            return userGroup;
          }),
        },
      };
    }

    case CompanyActionTypes.UPDATE_USER_GROUP_PROFILES: {
      const { userGroupId, profileIds } = payload as { userGroupId: number; profileIds: number[] };
      const isSpotGroup = !!state.userGroups.spotUserGroupsById[userGroupId];
      const newState: CompanyState = getNewImmutableState(state);
      return {
        ...newState,
        userGroups: {
          ...newState.userGroups,
          spotUserGroupsById: isSpotGroup
            ? {
                ...newState.userGroups.spotUserGroupsById,
                [userGroupId]: {
                  ...newState.userGroups.spotUserGroupsById[userGroupId],
                  profiles: [
                    ...(newState.userGroups.spotUserGroupsById[userGroupId].profiles || []),
                    ...profileIds,
                  ],
                },
              }
            : newState.userGroups.spotUserGroupsById,
          deferredUserGroupsById: !isSpotGroup
            ? {
                ...newState.userGroups.deferredUserGroupsById,
                [userGroupId]: {
                  ...newState.userGroups.deferredUserGroupsById[userGroupId],
                  profiles: profileIds,
                },
              }
            : newState.userGroups.deferredUserGroupsById,
          combinedUserGroups: newState.userGroups.combinedUserGroups.map((userGroup) => {
            if (userGroup.id === userGroupId) {
              return { ...userGroup, profiles: [...(userGroup.profiles || []), ...profileIds] };
            }
            return userGroup;
          }),
        },
      };
    }

    case CompanyActionTypes.ADD_USER_GROUP: {
      const payload = action.payload as UserGroupWithKind;

      return {
        ...state,
        userGroups: {
          ...state.userGroups,
          userGroups: {
            ...state.userGroups.userGroups,
            spot_user_groups: [
              ...state.userGroups.userGroups.spot_user_groups,
              ...(payload.user_group_kind === UserGroupKindEnum.Spot ? [payload] : []),
            ],
            deferred_user_groups: [
              ...state.userGroups.userGroups.deferred_user_groups,
              ...(payload.user_group_kind === UserGroupKindEnum.Deferred ? [payload] : []),
            ],
          },
          spotUserGroupsById: {
            ...state.userGroups.spotUserGroupsById,
            ...(payload.user_group_kind === UserGroupKindEnum.Spot
              ? { [payload.id]: payload }
              : {}),
          },
          deferredUserGroupsById: {
            ...state.userGroups.deferredUserGroupsById,
            ...(payload.user_group_kind === UserGroupKindEnum.Deferred
              ? { [payload.id]: payload }
              : {}),
          },
          combinedUserGroups: [...state.userGroups.combinedUserGroups, payload],
          allUserGroupNamesById: {
            ...state.userGroups.allUserGroupNamesById,
            [payload.id]: payload.name,
          },
        },
      };
    }

    case CompanyActionTypes.DELETE_USER_GROUP: {
      return {
        ...state,
        userGroups: {
          ...state.userGroups,
          userGroups: {
            ...state.userGroups.userGroups,
            spot_user_groups: state.userGroups.userGroups.spot_user_groups.filter(
              (userGroup) => userGroup.id !== payload
            ),
            deferred_user_groups: state.userGroups.userGroups.deferred_user_groups.filter(
              (userGroup) => userGroup.id !== payload
            ),
          },
          spotUserGroupsById: Object.fromEntries(
            state.userGroups.userGroups.spot_user_groups
              .filter((userGroup) => userGroup.id !== payload)
              .map((userGroup) => [userGroup.id, userGroup])
          ),
          deferredUserGroupsById: Object.fromEntries(
            state.userGroups.userGroups.deferred_user_groups
              .filter((userGroup) => userGroup.id !== payload)
              .map((userGroup) => [userGroup.id, userGroup])
          ),
          combinedUserGroups: state.userGroups.combinedUserGroups.filter(
            (userGroup) => userGroup.id !== payload
          ),
        },
      };
    }
    case CompanyActionTypes.SET_CHARGE_POINT_GROUPS: {
      return {
        ...state,
        chargePointGroups: {
          ...state.chargePointGroups,
          chargePointGroups: payload as ChargepointGroup[],
          byId: Object.fromEntries(
            (payload as ChargepointGroup[]).map((chargePointGroup) => [
              chargePointGroup.id,
              chargePointGroup,
            ])
          ),
          allChargePointGroupNamesById: Object.fromEntries(
            (payload as ChargepointGroup[]).map((chargePointGroup) => [
              chargePointGroup.id,
              chargePointGroup.name,
            ])
          ),
        },
      };
    }
    case CompanyActionTypes.DELETE_CHARGE_POINT_GROUP: {
      return {
        ...state,
        chargePointGroups: {
          ...state.chargePointGroups,
          chargePointGroups: state.chargePointGroups.chargePointGroups.filter(
            (chargePointGroup) => chargePointGroup.id !== payload
          ),
        },
      };
    }
    case CompanyActionTypes.ADD_CHARGE_POINT_GROUP: {
      return {
        ...state,
        chargePointGroups: {
          ...state.chargePointGroups,
          chargePointGroups: [
            ...state.chargePointGroups.chargePointGroups,
            payload as ChargepointGroup,
          ],
        },
      };
    }
    case CompanyActionTypes.SET_CONTRACTS: {
      return {
        ...state,
        contracts: {
          ...state.contracts,
          contracts: payload as Contract[],
          byId: Object.fromEntries(
            (payload as Contract[]).map((contract) => [contract.id, contract])
          ),
          allContractNamesById: Object.fromEntries(
            (payload as Contract[]).map((contract) => [contract.id, contract.verbose_name ?? null])
          ),
        },
      };
    }
    case CompanyActionTypes.SET_IS_FETCHING_USERS: {
      return { ...state, isFetching: { ...state.isFetching, users: payload as boolean } };
    }
    case CompanyActionTypes.SET_IS_FETCHING_USER_GROUPS: {
      return { ...state, isFetching: { ...state.isFetching, userGroups: payload as boolean } };
    }
    case CompanyActionTypes.SET_IS_FETCHING_USER_GROUP: {
      return { ...state, isFetching: { ...state.isFetching, userGroup: payload as boolean } };
    }
    case CompanyActionTypes.SET_IS_FETCHING_CHARGE_POINT_GROUPS: {
      return {
        ...state,
        isFetching: { ...state.isFetching, chargePointGroups: payload as boolean },
      };
    }
    case CompanyActionTypes.SET_IS_FETCHING_CONTRACTS: {
      return { ...state, isFetching: { ...state.isFetching, contracts: payload as boolean } };
    }
    case CompanyActionTypes.SET_IS_FETCHING_WHITE_LABEL: {
      return { ...state, isFetching: { ...state.isFetching, whiteLabel: payload as boolean } };
    }
    case CompanyActionTypes.ADD_CONTRACT: {
      return {
        ...state,
        contracts: {
          ...state.contracts,
          contracts: [...state.contracts.contracts, payload as Contract],
        },
      };
    }
    case CompanyActionTypes.UPDATE_CONTRACT: {
      const p = payload as { contractId: number; contract: Contract };
      const contractsUpdated = state.contracts.contracts.map((contract) => {
        if (contract.id === p.contractId) {
          contract = p.contract;
        }

        return contract;
      });

      return { ...state, contracts: { ...state.contracts, contracts: contractsUpdated } };
    }
    case CompanyActionTypes.DELETE_CONTRACT: {
      const p = payload as { contractId: number };
      return {
        ...state,
        contracts: {
          ...state.contracts,
          contracts: state.contracts.contracts.filter((contract) => contract.id !== p.contractId),
        },
      };
    }
    case CompanyActionTypes.SET_POWER_CATEGORY: {
      return {
        ...state,
        powerCategory: { ...state.powerCategory, powerCategory: payload as PowerCategory[] },
      };
    }
    case CompanyActionTypes.SET_WHITE_LABEL: {
      return { ...state, whiteLabelConfig: payload as WhiteLabelConfig };
    }
    default: {
      return state;
    }
  }
}

interface FetchCmsUsersParams {
  page?: number;
  page_size?: number;
  email__contains?: string;
  group__in?: number[];
  id__in?: number[];
  is_active?: boolean;
  is_mobile?: boolean;
  RFID?: string;
  document_type__in?: DocumentTypesEnum[];
}

export const fetchCmsUsers = (params: FetchCmsUsersParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsFetchingUsers(true));
  const queryParams: FetchCmsUsersParams = {
    page: params.page || 1,
    page_size: params.page_size || 10,
    ...params,
  };
  const apiUsers = `/accounts/cms/users/?${Object.entries(queryParams)
    .map(([key, value]) => `${key}=${Array.isArray(value) ? value.join(',') : value}`)
    .join('&')}`;

  const resolve = (data: User[]) => {
    return data;
  };

  const reject = (error: AxiosError) => {
    return error;
  };

  return callNextPage(apiUsers, resolve, reject);
};

export const fetchUsers = () => async (dispatch: AppDispatch) => {
  dispatch(setIsFetchingUsers(true));

  const apiUsers = `/accounts/user/?page_size=500`;

  const resolve = (data: User[]) => {
    dispatch(setUsers(data));
    dispatch(setIsFetchingUsers(false));

    return data;
  };

  const reject = (error: AxiosError) => {
    dispatch(setIsFetchingUsers(false));

    return error;
  };

  return callNextPage(apiUsers, resolve, reject);
};

export const fetchUserGroups = () => async (dispatch: AppDispatch) => {
  dispatch(setIsFetchingUserGroups(true));

  return await getUserGroups()
    .then((res) => {
      if (res.data) {
        dispatch(setUserGroups(res.data));
        return res;
      }
      return res;
    })
    .catch((err) => {
      log.error(err);
      return err;
    })
    .finally(() => dispatch(setIsFetchingUserGroups(false)));
};

export const fetchUserGroupById = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsFetchingUserGroup(true));

  return await getUserGroupById(id)
    .then((res) => {
      if (res.data) {
        dispatch(setSelectedUserGroup(res.data));
        return res.data;
      }
      return res;
    })
    .catch((err) => {
      log.error(err);
      return err;
    })
    .finally(() => dispatch(setIsFetchingUserGroup(false)));
};
export const fetchProfileUserGroups = () => async (dispatch: AppDispatch) => {
  const spotProfileUserGroups = (await getSpotProfileUserGroups()).data?.results;
  const deferredProfileUserGroups = (await getDeferredProfileUserGroups()).data?.results;
  if (!spotProfileUserGroups || !deferredProfileUserGroups) return;
  return dispatch(setProfileUserGroups(spotProfileUserGroups, deferredProfileUserGroups));
};

export const removeUsersFromGroup =
  (group: UserGroupWithKind, userProfiles: CMSProfile[], queryKey: Array<string | number>) =>
  async (dispatch: AppDispatch, getState: () => { company: CompanyState }) => {
    const groupId = group.id;
    const isSpotGroup = group.user_group_kind === UserGroupKindEnum.Spot;

    const state = getState();
    const userGroupsState = state.company.userGroups;

    const postBatch: BatchCall<
      Awaited<ReturnType<typeof postProfileSpotUserGroup>>,
      { user_group: number; profile: number }
    > = [];

    const deleteBatch: BatchCall<
      Awaited<
        ReturnType<typeof deleteProfileSpotUserGroup | typeof deleteProfileDeferredUserGroup>
      >,
      number
    > = [];
    const profileIdsToDelete: number[] = [];

    // Default user group is always the default spot group
    const defaultUserGroup = userGroupsState.defaultGroups.spotUserGroup as UserGroup;
    userProfiles.forEach((profile) => {
      const profileUserGroups = isSpotGroup
        ? profile?.spot_user_groups
        : profile?.deferred_user_groups;

      const profileUserGroupToDelete = profileUserGroups?.find(
        (profileUserGroup) => profileUserGroup.user_group === groupId
      );

      if (!profileUserGroupToDelete) return;

      // Users selected that are not already in the default group will be added
      const defaultGroupIncludesUser = defaultUserGroup?.profiles.includes(profile.user);

      if (!defaultGroupIncludesUser) {
        postBatch.push({
          method: postProfileSpotUserGroup,
          args: { user_group: defaultUserGroup?.id, profile: profile.user },
        });
      }

      // Users selected will be deleted from the corresponding group
      isSpotGroup
        ? deleteBatch.push({
            method: deleteProfileSpotUserGroup,
            args: profileUserGroupToDelete.id,
          })
        : deleteBatch.push({
            method: deleteProfileDeferredUserGroup,
            args: profileUserGroupToDelete.id,
          });
    });

    // Add users to default group
    let successfulPosts = 0;
    let postErrors = 0;
    const postResults = await callBatch(postBatch);
    postResults.forEach((res) => {
      if (res.status < 200 || res.status >= 300) {
        log.error(`Error adding user to default spot group ${defaultUserGroup?.id}`);
        postErrors++;
        return;
      }
      dispatch(addProfileToProfileUserGroups(defaultUserGroup?.id, res.data));
      successfulPosts++;
    });

    // Delete users from the corresponding group
    const deleteResults = await callBatch(deleteBatch);
    let successfulDeletes = 0;
    let deleteErrors = 0;
    deleteResults.forEach((res, index) => {
      if (res.status < 200 || res.status >= 300) {
        deleteErrors++;
        log.error(`Error removing user from ${isSpotGroup ? 'spot' : 'deferred'} group ${groupId}`);
        return;
      }
      successfulDeletes++;
      profileIdsToDelete.push(deleteBatch[index].args);
    });
    if (successfulDeletes > 0)
      dispatch(showSnackbarMessage(`Se eliminaron ${successfulDeletes} usuarios del grupo.`));
    if (deleteErrors > 0)
      dispatch(showSnackbarMessage(`No se pudo eliminar a ${deleteErrors} usuarios del grupo.`));
    if (successfulPosts > 0)
      dispatch(
        showSnackbarMessage(
          `Se añadieron ${successfulPosts} usuarios al grupo por defecto ${defaultUserGroup?.name}.`
        )
      );
    if (postErrors > 0)
      dispatch(
        showSnackbarMessage(`No se pudo añadir a ${postErrors} usuarios al grupo por defecto.`)
      );
    profileIdsToDelete.forEach((profileId) => {
      dispatch(removeProfileFromProfileUserGroups(groupId, profileId));
    });
    await queryClient.invalidateQueries({
      queryKey,
    });
  };

export const fetchChargePointGroups = () => async (dispatch: AppDispatch) => {
  dispatch(setIsFetchingChargePointGroups(true));
  try {
    const res = await getChargePointGroups();
    if (res.data.results.length) dispatch(setChargePointGroups(res.data.results));
    return res;
  } catch (err) {
    log.error(err);
    return err;
  } finally {
    dispatch(setIsFetchingChargePointGroups(false));
  }
};

export const fetchContracts = () => async (dispatch: AppDispatch) => {
  dispatch(setIsFetchingContracts(true));

  return await getContracts()
    .then((res) => {
      if (res.data.results.length) {
        dispatch(setContracts(res.data.results));
        return res;
      }
      return res;
    })
    .catch((err) => {
      log.error(err);
      return err;
    })
    .finally(() => dispatch(setIsFetchingContracts(false)));
};

export const addContractApi = (contract: Form) => async (dispatch: AppDispatch) => {
  try {
    const res = await createContract(contract);
    if (res.data) {
      dispatch(addContract(res.data));
      return res;
    }
  } catch (err) {
    return err;
  }
};

export const updateContractApi =
  ({ contractId, contract }: SubmitContractData) =>
  async (dispatch: AppDispatch) => {
    if (!contractId) return;
    try {
      const res = await updateContract(contractId, contract);
      if (res.data) {
        dispatch(editContract(contractId, res.data));
        return res;
      }
    } catch (err) {
      return err;
    }
  };

export const deleteContractApi = (contractId: number) => async (dispatch: AppDispatch) => {
  try {
    const res = await deleteContract(contractId);

    if (res.status >= 200 && res.status < 300) {
      dispatch(removeContract(contractId));
      return res;
    }
  } catch (err) {
    if (err instanceof AxiosError) return err;
  }
};

export const fetchPowerCategory = () => async (dispatch: AppDispatch) => {
  return await getPowerCategory()
    .then((res) => {
      if (res.data.results.length) return dispatch(setPowerCategory(res.data.results));
      return res;
    })
    .catch((err) => {
      log.error(err);
      return err;
    });
};

export const fetchWhiteLabel = () => async (dispatch: AppDispatch) => {
  try {
    const res = await getWhiteLabelVariables();
    if (res.data) dispatch(setWhiteLabelConfig(res.data));
    return res;
  } catch (err) {
    return err;
  }
};

export const selectUsers = (state: RootState) => state.company.users.users;
export const selectUsersAllById = (state: RootState) => state.company.users.byId;
export const selectAllUserEmailsById = (state: RootState) => state.company.users.allUserEmailsById;

// User Group
export const selectUserGroups = (state: RootState) => state.company.userGroups.userGroups;
export const selectSpotUserGroupsById = (state: RootState) =>
  state.company.userGroups.spotUserGroupsById;
export const selectDeferredUserGroupsById = (state: RootState) =>
  state.company.userGroups.deferredUserGroupsById;
export const selectCombinedUserGroups = (state: RootState) =>
  state.company.userGroups.combinedUserGroups;
export const selectCombinedUserGroupsById = (state: RootState) =>
  state.company.userGroups.combinedUserGroupsById;
export const selectDefaultUserGroups = (state: RootState) => state.company.userGroups.defaultGroups;
export const selectAllUserGroupNamesById = (state: RootState) =>
  state.company.userGroups.allUserGroupNamesById;
export const selectSelectedUserGroup = (state: RootState) =>
  state.company.userGroups.selectedUserGroup;

// Charge Points Groups
export const selectChargePointGroups = (state: RootState) =>
  state.company.chargePointGroups.chargePointGroups;
export const selectChargePointGroupsById = (state: RootState) =>
  state.company.chargePointGroups.byId;
// export const selectChargePointGroupsById = (state: RootState, id: number) =>
//   state.company.chargePointGroups.byId[id];
export const selectDefaultChargePointGroup = (state: RootState) =>
  state.company.chargePointGroups.defaultChargePointGroup;
export const selectAllChargePointGroupNamesById = (state: RootState) =>
  state.company.chargePointGroups.allChargePointGroupNamesById;

// Contracts
export const selectContracts = (state: RootState) => state.company.contracts.contracts;
export const selectContractsById = (state: RootState) => state.company.contracts.byId;
export const selectAllContractNamesById = (state: RootState) =>
  state.company.contracts.allContractNamesById;

// White Label
export const selectWhiteLabelConfig = (state: RootState) => state.company.whiteLabelConfig;

// Fetchings
export const selectIsFetchingUsers = (state: RootState) => state.company.isFetching.users;
export const selectIsFetchingUserGroups = (state: RootState) => state.company.isFetching.userGroups;
export const selectIsFetchingUserGroup = (state: RootState) => state.company.isFetching.userGroup;
export const selectIsFetchingChargePointGroups = (state: RootState) =>
  state.company.isFetching.chargePointGroups;
export const selectIsFetchingContracts = (state: RootState) => state.company.isFetching.contracts;
export const selectPowerCategory = (state: RootState) => state.company.powerCategory.powerCategory;
export const selectIsFetchingWhiteLabels = (state: RootState) =>
  state.company.isFetching.whiteLabel;
